import React, { useState, useEffect } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, AlertCircle, Info, DollarSign, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WithdrawalRequestPage = () => {
  const [currencies, setCurrencies] = useState([]);
  const [limits, setLimits] = useState(null);
  const [form, setForm] = useState({
    asset_symbol: '',
    amount: '',
    destination_address: '',
    network: 'polygon'
  });
  const [feeCalc, setFeeCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const [networks, setNetworks] = useState([
    { id: 'polygon', name: 'Polygon (MATIC)' },
    { id: 'ethereum', name: 'Ethereum (ETH)' }
    // 💡 Add more networks here:
    // { id: 'arbitrum', name: 'Arbitrum' },
    // { id: 'optimism', name: 'Optimism' },
    // { id: 'base', name: 'Base' },
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (form.asset_symbol && form.amount && parseFloat(form.amount) > 0) {
      calculateFees();
    } else {
      setFeeCalc(null);
    }
  }, [form.asset_symbol, form.amount, form.network]);

  const fetchData = async () => {
    try {
      const [currenciesRes, limitsRes] = await Promise.all([
        fetch(`${API_URL}/api/exchange/currencies`, { credentials: 'include' }),
        fetch(`${API_URL}/api/user/withdrawals/limits/current`, { credentials: 'include' })
      ]);

      const currenciesData = await currenciesRes.json();
      const limitsData = await limitsRes.json();

      setCurrencies(currenciesData.currencies || []);
      setLimits(limitsData);
    } catch (error) {
      toast.error('Failed to load withdrawal data');
    }
  };

  const calculateFees = async () => {
    setCalculating(true);
    try {
      const response = await fetch(`${API_URL}/api/user/withdrawals/calculate-fees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset_symbol: form.asset_symbol,
          amount: parseFloat(form.amount),
          network: form.network
        })
      });

      if (!response.ok) throw new Error('Fee calculation failed');

      const data = await response.json();
      setFeeCalc(data.fees);
    } catch (error) {
      setFeeCalc(null);
    } finally {
      setCalculating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/user/withdrawals/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          asset_symbol: form.asset_symbol,
          amount: parseFloat(form.amount),
          destination_address: form.destination_address,
          network: form.network,
          source_type: 'internal_balance'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Withdrawal request failed');
      }

      toast.success('Withdrawal request created successfully!');
      
      // Reset form
      setForm({
        asset_symbol: '',
        amount: '',
        destination_address: '',
        network: 'polygon'
      });
      setFeeCalc(null);
      
      // Refresh limits
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCurrency = currencies.find(c => c.symbol === form.asset_symbol);

  return (
    <FocusedLayout>
      <div className="max-w-2xl mx-auto space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Withdraw Funds</h1>
          <p className="text-muted-foreground">Send cryptocurrency to external wallets</p>
        </div>

        {/* Limits Info */}
        {limits && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-2">
                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-900">
                  <strong>Your Limits:</strong> Daily: ${limits.available.daily_available.toFixed(2)} | 
                  Weekly: ${limits.available.weekly_available.toFixed(2)} | 
                  Per Transaction: ${limits.available.per_transaction_max.toFixed(2)}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Withdrawal Form */}
        <Card>
          <CardHeader>
            <CardTitle>Withdrawal Details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Asset Selection */}
              <div className="space-y-2">
                <Label>Asset *</Label>
                <select
                  value={form.asset_symbol}
                  onChange={(e) => setForm({...form, asset_symbol: e.target.value})}
                  className="w-full border rounded-md p-2"
                  required
                >
                  <option value="">Select asset</option>
                  {currencies.map(currency => (
                    <option key={currency.symbol} value={currency.symbol}>
                      {currency.symbol} - Balance: {currency.balance || 0}
                    </option>
                  ))}
                </select>
              </div>

              {/* Network Selection */}
              <div className="space-y-2">
                <Label>Network *</Label>
                <select
                  value={form.network}
                  onChange={(e) => setForm({...form, network: e.target.value})}
                  className="w-full border rounded-md p-2"
                  required
                >
                  {networks.map(net => (
                    <option key={net.id} value={net.id}>{net.name}</option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="any"
                    value={form.amount}
                    onChange={(e) => setForm({...form, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                  {selectedCurrency && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {selectedCurrency.symbol}
                    </span>
                  )}
                </div>
                {selectedCurrency && (
                  <p className="text-xs text-muted-foreground">
                    Available: {selectedCurrency.balance || 0} {selectedCurrency.symbol}
                  </p>
                )}
              </div>

              {/* Destination Address */}
              <div className="space-y-2">
                <Label>Destination Address *</Label>
                <Input
                  type="text"
                  value={form.destination_address}
                  onChange={(e) => setForm({...form, destination_address: e.target.value})}
                  placeholder="0x..."
                  className="font-mono text-sm"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Enter the {form.network} address where you want to receive funds
                </p>
              </div>

              {/* Fee Preview */}
              {feeCalc && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                  <div className="font-semibold text-sm">Fee Breakdown</div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Withdrawal Amount:</span>
                      <span className="font-medium">
                        {feeCalc.gross_amount} {form.asset_symbol}
                      </span>
                    </div>
                    
                    <div className="flex justify-between text-orange-600">
                      <span>Network Fee:</span>
                      <span>{feeCalc.fees.network_fee.toFixed(8)} {form.asset_symbol}</span>
                    </div>
                    
                    <div className="flex justify-between text-orange-600">
                      <span>Platform Fee ({feeCalc.fees.platform_fee_percentage}%):</span>
                      <span>{feeCalc.fees.platform_fee.toFixed(8)} {form.asset_symbol}</span>
                    </div>
                    
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>You'll Receive:</span>
                      <span className="text-green-600">
                        {feeCalc.net_amount.toFixed(8)} {form.asset_symbol}
                      </span>
                    </div>
                    
                    <div className="text-xs text-muted-foreground">
                      ≈ ${feeCalc.net_amount_usd.toFixed(2)} USD
                    </div>
                  </div>
                </div>
              )}

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-900">
                    <strong>Important:</strong> Withdrawals may require up to 24 hours cool-down period and admin approval for large amounts. 
                    Ensure the destination address is correct - transactions cannot be reversed.
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={loading || calculating || !feeCalc}
              >
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    <TrendingDown className="h-4 w-4 mr-2" />
                    Request Withdrawal
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </FocusedLayout>
  );
};